import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Star, Clock, Utensils } from 'lucide-react';

export const LosAngeles: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Best Chinese Food in Los Angeles | Chinese Food Maps</title>
        <meta name="description" content="Discover the best Chinese restaurants in Los Angeles. From authentic Sichuan cuisine in San Gabriel Valley to dim sum in Monterey Park, explore LA's diverse Chinese food scene." />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-red-900">Best Chinese Food in Los Angeles</h1>

        <div className="prose max-w-none text-gray-600 mb-12">
          <p className="text-lg leading-relaxed">
            Los Angeles is home to one of the most diverse Chinese food scenes in America, particularly in the San Gabriel Valley. From traditional dim sum palaces to regional specialties from every corner of China, LA offers an unparalleled variety of authentic Chinese cuisine.
          </p>
        </div>

        <h2 className="text-2xl font-bold mb-8 text-black">Top Rated Chinese Food Restaurants</h2>

        <div className="grid gap-8">
          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-semibold text-red-800 mb-6">San Gabriel Valley</h2>
            
            <div className="space-y-8">
              {/* Din Tai Fung */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Din Tai Fung</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.7</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>1108 S Baldwin Ave, Arcadia, CA 91007</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(626) 574-7068</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Taiwanese-Style Soup Dumplings</li>
                    <li>Noodles and Dumplings</li>
                    <li>Michelin-Recommended</li>
                    <li>Precise Culinary Techniques</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Xiao Long Bao (Soup Dumplings)</li>
                    <li>Spicy Wontons</li>
                    <li>Truffle Xiao Long Bao</li>
                    <li>Green Beans with Garlic</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=1108+S+Baldwin+Ave+Arcadia+CA+91007`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:6265747068"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>

              {/* Sichuan Impression */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Sichuan Impression</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.6</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>1900 W Valley Blvd, Alhambra, CA 91803</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(626) 283-4622</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Authentic Sichuan Cuisine</li>
                    <li>Modern Presentation</li>
                    <li>House-made Chili Oil</li>
                    <li>Traditional Spices</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Toothpick Lamb</li>
                    <li>Tea-Smoked Ribs</li>
                    <li>Mapo Tofu</li>
                    <li>Boiled Fish with Green Peppercorns</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=1900+W+Valley+Blvd+Alhambra+CA+91803`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:6262834622"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>

              {/* Sea Harbour */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Sea Harbour</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.5</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>3939 Rosemead Blvd, Rosemead, CA 91770</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(626) 288-3939</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>High-End Dim Sum</li>
                    <li>Cantonese Seafood</li>
                    <li>Made-to-Order Dim Sum</li>
                    <li>Banquet Specialties</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Truffle Siu Mai</li>
                    <li>Baked BBQ Pork Buns</li>
                    <li>Live Seafood Preparations</li>
                    <li>Egg Custard Tarts</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=3939+Rosemead+Blvd+Rosemead+CA+91770`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:6262883939"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>

              {/* Chengdu Taste */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Chengdu Taste</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.6</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>828 W Valley Blvd, Alhambra, CA 91803</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(626) 588-2284</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Authentic Sichuan Cuisine</li>
                    <li>Spicy Specialties</li>
                    <li>Traditional Chengdu Dishes</li>
                    <li>House-made Seasonings</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Diced Rabbit with Younger Sister's Secret Recipe</li>
                    <li>Toothpick Lamb with Cumin</li>
                    <li>Mung Bean Jelly Noodles</li>
                    <li>Water Boiled Fish</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=828+W+Valley+Blvd+Alhambra+CA+91803`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:6265882284"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>

              {/* Elite Restaurant */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Elite Restaurant</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.4</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>700 S Atlantic Blvd, Monterey Park, CA 91754</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(626) 282-9088</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Traditional Dim Sum</li>
                    <li>Cantonese Cuisine</li>
                    <li>Seafood Dishes</li>
                    <li>Banquet Service</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Har Gow (Shrimp Dumplings)</li>
                    <li>Crispy Roasted Pork Belly</li>
                    <li>Egg Custard Buns</li>
                    <li>Pan-Fried Turnip Cake</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=700+S+Atlantic+Blvd+Monterey+Park+CA+91754`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:6262829088"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-semibold text-red-800 mb-6">LA Chinese Food Fun Facts</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Regional Hub</h3>
                  <p className="text-gray-700">The San Gabriel Valley is home to the largest Chinese community in the United States outside of New York City.</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Culinary Diversity</h3>
                  <p className="text-gray-700">LA's Chinese food scene represents all eight major Chinese regional cuisines, from Sichuan to Cantonese.</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Modern Evolution</h3>
                  <p className="text-gray-700">The area has become known for innovative Chinese-American fusion cuisine and modern interpretations of traditional dishes.</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Food Culture</h3>
                  <p className="text-gray-700">Many restaurants in SGV specialize in specific regional dishes, making it a destination for authentic Chinese cuisine.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-semibold text-red-800 mb-6">Best Times to Visit</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-semibold mb-3">Peak Hours</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <Clock size={18} className="text-red-600" />
                    Dim Sum: 10 AM - 2 PM (weekends)
                  </li>
                  <li className="flex items-center gap-2">
                    <Clock size={18} className="text-red-600" />
                    Dinner: 6 PM - 9 PM
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-3">Special Events</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <Utensils size={18} className="text-red-600" />
                    Lunar New Year Festival (Jan/Feb)
                  </li>
                  <li className="flex items-center gap-2">
                    <Utensils size={18} className="text-red-600" />
                    Mid-Autumn Festival (Sept)
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};